import * as React from 'react'
import * as TabsPrimitive from '@radix-ui/react-tabs'

import { cn } from '../../lib/utils'

const Tabs = TabsPrimitive.Root

const TabsList = React.forwardRef(({ className, ...props }, ref) => (
  <div className='relative w-full'>
    <TabsPrimitive.List
      ref={ref}
      className={cn(
        'fabits-pill-list inline-flex h-auto w-full items-center justify-center overflow-x-auto rounded-[999px] border p-1',
        className,
      )}
      style={{
        scrollbarWidth: 'none' /* Firefox */,
        msOverflowStyle: 'none' /* IE and Edge */,
      }}
      {...props}
    />
  </div>
))
TabsList.displayName = TabsPrimitive.List.displayName

const TabsTrigger = React.forwardRef(({ className, onClick, ...props }, ref) => {
  // Create a ref we can use to scroll this element into view
  const triggerRef = React.useRef(null)

  // Combine the external ref with our local ref
  const handleRef = (element) => {
    // Update our local ref
    triggerRef.current = element

    // Forward the ref if it exists
    if (typeof ref === 'function') {
      ref(element)
    } else if (ref) {
      ref.current = element
    }
  }

  // Handle click to scroll into view
  const handleClick = (e) => {
    // Call the original onClick if it exists
    if (onClick) {
      onClick(e)
    }

    // Scroll the tab into view with a slight delay to ensure state changes have occurred
    setTimeout(() => {
      if (triggerRef.current) {
        triggerRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'nearest',
          inline: 'center',
        })
      }
    }, 10)
  }

  return (
    <TabsPrimitive.Trigger
      ref={handleRef}
      className={cn(
        'fabits-pill-trigger inline-flex h-10 min-w-0 flex-1 basis-0 items-center justify-center whitespace-nowrap border border-transparent px-4 text-center text-sm font-semibold ring-offset-background transition-colors duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-600 focus-visible:ring-offset-2 disabled:pointer-events-none disabled:opacity-50 data-[state=active]:bg-primary-600 data-[state=active]:text-white data-[state=active]:shadow-[0_8px_22px_rgba(40,24,255,0.18)]',
        className,
      )}
      onClick={handleClick}
      {...props}
    />
  )
})
TabsTrigger.displayName = TabsPrimitive.Trigger.displayName

const TabsContent = React.forwardRef(({ className, ...props }, ref) => (
  <TabsPrimitive.Content
    ref={ref}
    className={cn(
      'mt-2 ring-offset-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
      className,
    )}
    {...props}
  />
))
TabsContent.displayName = TabsPrimitive.Content.displayName

export { Tabs, TabsList, TabsTrigger, TabsContent }
